import { LogLevel } from '@datocms/cma-client-browser';
import { createDatoClient } from '@src/utility/dato';
import { Button, Canvas, FieldError, FieldHint } from 'datocms-react-ui';
import { useEffect, useState } from 'react';

import type { AnyElement } from '@src/components/sidepanel/CompositionSettings/types';
import type { RenderFieldExtensionCtx } from 'datocms-plugin-sdk';

type PropTypes = {
  ctx: RenderFieldExtensionCtx;
};
const errorMessage =
  'Tracks in the source are not in the correct order. This could cause errors with controls behavior.';

export default function SortSource({ ctx }: PropTypes) {
  const [hasError, setHasError] = useState<boolean>(false);
  const [sortedSource, setSortedSource] = useState<AnyElement>(
    {} as AnyElement,
  );

  const dClient = createDatoClient({
    apiToken: ctx.currentUserAccessToken || '',
    environment: ctx.environment,
    logLevel: LogLevel.NONE,
  });

  const handleSortSource = async () => {
    if (ctx.item?.id) {
      try {
        await dClient.items.update(ctx.item.id, {
          source: JSON.stringify(sortedSource),
        });
        ctx.notice(
          'Source elements being sorted successfully! Please check if controls are still valid or re-generate them!',
        );
      } catch (error) {
        console.error('Error sorting source:', error);
        ctx.alert('Failed to automatically sort source');
      }
    }
  };

  const checkSourceOrder = (sourceContext: any) => {
    if (
      !sourceContext ||
      !sourceContext?.elements ||
      !Array.isArray(sourceContext?.elements)
    ) {
      return;
    }

    const originalSource = sourceContext;
    const sortedSource = {
      ...originalSource,
      elements: sortSourceByTrackNumberRecursive(
        originalSource.elements as AnyElement[],
      ),
    };
    const hasError =
      JSON.stringify(originalSource) !== JSON.stringify(sortedSource);

    setSortedSource(sortedSource);
    setHasError(hasError);
    if (hasError) {
      ctx.alert(errorMessage);
    }
  };

  useEffect(() => {
    checkSourceOrder(
      JSON.parse((ctx.item?.attributes?.source as string) || '{}'),
    );
  }, [ctx.item?.attributes?.source]);

  return (
    <Canvas ctx={ctx}>
      {hasError ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <FieldError>{errorMessage}</FieldError>
          <Button type="button" buttonType="primary" onClick={handleSortSource}>
            Sort source elements
          </Button>
        </div>
      ) : (
        <FieldHint>Source elements are in the correct order.</FieldHint>
      )}
    </Canvas>
  );
}

function sortSourceByTrackNumberRecursive(source: AnyElement[]): AnyElement[] {
  for (const element of source) {
    if ('elements' in element && Array.isArray(element.elements)) {
      element.elements = sortSourceByTrackNumberRecursive(
        element.elements as AnyElement[],
      );
    }
  }
  return source
    .sort((a: AnyElement, b: AnyElement) => a?.track - b?.track)
    .map((elem, index) => ({ ...elem, track: index + 1 }));
}
